import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse, get } from '@truefit/http-utils';

interface PlaybackResponse {
  /** 'cloudfront' URLs are durable; 'presigned' ones expire. */
  method: 'cloudfront' | 'presigned';
  downloadUrl: string;
  expiresIn?: number;
}

/**
 * Resolves a URL the browser can stream the exported clip from.
 *
 * Same endpoint the download button uses, with `purpose=playback`: it answers
 * with the bucket's CloudFront URL when there is one, and otherwise a presigned
 * S3 URL carrying `Content-Disposition: inline`. The default `purpose=download`
 * sends `attachment` instead, which a browser saves to disk rather than handing
 * to a <video> — so the flag is load-bearing, not cosmetic.
 *
 * Deliberately not the clip's `preview.mp4Url`: that asset is documented as a
 * *short muted* teaser for hover, so a modal built on it would quietly play
 * something other than the clip.
 *
 * Nothing is retained between openings — the URL may be presigned, and a page
 * left open long enough would hand a dead link to the player.
 */
const useClipPlaybackQuery = (clipId: string | null): UseQueryResult<string, Error> =>
  useQuery({
    queryKey: ['clips', 'playback', clipId],
    queryFn: async () => {
      const { data }: AxiosResponse<PlaybackResponse> = await get(
        `/v1.0/clips/${clipId}/download?video=true&purpose=playback`,
      );
      if (!data?.downloadUrl) throw new Error('No playback URL was returned for this clip');
      return data.downloadUrl;
    },
    enabled: Boolean(clipId),
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    throwOnError: false,
  });

export default useClipPlaybackQuery;
